import { useEffect, useMemo, useState } from "react";
import { useAdminFactors } from "../hooks/useApi";
import AdminFactorDrawer, { AdminFactor } from "../components/AdminFactorDrawer";
import { formatToman, formatNumber, toPersianDigits } from "../utils/format";
import "../admin-theme.css";

const PAGE_SIZE = 50;

const STATUS_OPTIONS = [
  { value: "", label: "همه وضعیت‌ها" },
  { value: "successful", label: "موفق" },
  { value: "failed", label: "ناموفق" },
];

const SORT_OPTIONS = [
  { value: "date_desc", label: "جدیدترین اول" },
  { value: "date_asc", label: "قدیمی‌ترین اول" },
];

function FactorStatusBadge({ status }: { status?: string }) {
  switch (status) {
    case "verified":
      return <span className="admin-badge admin-badge-success">موفق</span>;
    case "pending":
      return <span className="admin-badge admin-badge-warning">در انتظار</span>;
    case "failed":
      return <span className="admin-badge admin-badge-error">ناموفق</span>;
    case "expired":
      return <span className="admin-badge admin-badge-warning">منقضی شده</span>;
    default:
      return <span className="admin-badge admin-badge-info">{status || "—"}</span>;
  }
}

function formatDate(dateStr?: string | null): string {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString("fa-IR", { timeZone: "Asia/Tehran" });
}

export default function AdminFactors() {
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [sort, setSort] = useState("date_desc");
  const [page, setPage] = useState(1);
  const [selectedFactor, setSelectedFactor] = useState<AdminFactor | null>(null);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  // Reset to the first page whenever filters change.
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, statusFilter, sort]);

  const params = useMemo(() => {
    const p: Record<string, unknown> = { sort, limit: PAGE_SIZE, offset: (page - 1) * PAGE_SIZE };
    if (debouncedSearch) p.search = debouncedSearch;
    if (statusFilter) p.status_filter = statusFilter;
    return p;
  }, [debouncedSearch, statusFilter, sort, page]);

  const { data, isLoading, isFetching } = useAdminFactors(params);

  const items: AdminFactor[] = data?.items ?? [];
  const total = data?.total_count ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div>
        <h1 className="text-2xl font-bold" style={{ color: "var(--admin-text-primary)" }}>فاکتورها</h1>
        <p className="text-sm mt-1" style={{ color: "var(--admin-text-secondary)" }}>
          تمام فاکتورهای پرداخت همه کاربران (شارژ کیف پول و خرید اشتراک)
        </p>
      </div>

      {/* Factors Table */}
      <div className="admin-card overflow-hidden">
        <div className="admin-card-header flex flex-wrap items-center gap-3 justify-between">
          <h2 className="admin-card-title">لیست فاکتورها</h2>
          <div className="flex flex-wrap items-center gap-3">
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="admin-input"
              style={{ width: 240 }}
              placeholder="جستجوی نام، نام خانوادگی، ایمیل یا موبایل..."
            />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="admin-input"
              style={{ width: 150 }}
            >
              {STATUS_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
            <select
              value={sort}
              onChange={(e) => setSort(e.target.value)}
              className="admin-input"
              style={{ width: 150 }}
            >
              {SORT_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
        </div>

        {isLoading && !items.length ? (
          <div className="flex items-center justify-center py-16">
            <svg className="animate-spin h-8 w-8" viewBox="0 0 24 24" fill="none" style={{ color: "var(--admin-primary)" }}>
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
          </div>
        ) : !items.length ? (
          <div className="flex flex-col items-center justify-center py-16" style={{ color: "var(--admin-text-tertiary)" }}>
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" className="mb-3 opacity-50" stroke="currentColor" strokeWidth="1.5">
              <rect x="2" y="5" width="20" height="14" rx="2" />
              <path d="M2 10h20" />
              <path d="M6 15h4" />
            </svg>
            <p className="text-sm">هیچ فاکتوری یافت نشد</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>کد</th>
                  <th>کاربر</th>
                  <th>ایمیل</th>
                  <th>شماره موبایل</th>
                  <th>مبلغ (تومان)</th>
                  <th>وضعیت</th>
                  <th>تاریخ ایجاد</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {items.map((f) => {
                  const u = f.user;
                  const isLegal = u?.person_type === "legal";
                  const displayName = isLegal
                    ? u?.company_name || u?.name || u?.email || "—"
                    : [u?.name, u?.family_name].filter(Boolean).join(" ") || u?.email || "—";
                  return (
                    <tr
                      key={f.id}
                      onClick={() => setSelectedFactor(f)}
                      className="cursor-pointer"
                      title="مشاهده فاکتور"
                    >
                      <td>
                        <span className="font-number text-sm" dir="ltr" style={{ color: "var(--admin-text-secondary)" }}>
                          #{f.id}
                        </span>
                      </td>
                      <td>
                        <span className="text-sm font-medium block" style={{ color: "var(--admin-text-primary)" }}>
                          {displayName}
                        </span>
                        {u?.username ? (
                          <span dir="ltr" className="font-mono text-xs block" style={{ color: "var(--admin-text-tertiary)" }}>
                            @{u.username}
                          </span>
                        ) : null}
                      </td>
                      <td>
                        <span dir="ltr" className="font-mono text-xs block" style={{ color: "var(--admin-text-secondary)" }}>
                          {u?.email || "—"}
                        </span>
                      </td>
                      <td>
                        <span dir="ltr" className="font-mono text-xs block" style={{ color: "var(--admin-text-secondary)" }}>
                          {u?.phone ? toPersianDigits(u.phone) : "—"}
                        </span>
                      </td>
                      <td>
                        <span className="font-number text-sm" style={{ color: "var(--admin-text-primary)" }}>
                          {formatToman(f.amount)}
                        </span>
                      </td>
                      <td>
                        <FactorStatusBadge status={f.status} />
                      </td>
                      <td>
                        <span className="text-xs" style={{ color: "var(--admin-text-secondary)" }}>
                          {formatDate(f.created_at)}
                        </span>
                      </td>
                      <td className="text-left">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedFactor(f);
                          }}
                          className="p-1.5 rounded-lg transition-colors hover:opacity-70"
                          style={{ color: "var(--admin-primary)" }}
                          title="مشاهده فاکتور"
                        >
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                            <circle cx="12" cy="12" r="3" />
                          </svg>
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {/* Footer: count + pagination */}
            <div className="px-4 py-3 border-t flex items-center justify-between flex-wrap gap-3" style={{ borderColor: "var(--admin-border)" }}>
              <span className="text-xs" style={{ color: "var(--admin-text-tertiary)" }}>
                {formatNumber(total)} فاکتور
                {isFetching ? <span className="mr-2">در حال بارگذاری…</span> : null}
              </span>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  className="admin-btn admin-btn-secondary admin-btn-sm"
                  disabled={page <= 1 || isFetching}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                >
                  قبلی
                </button>
                <span className="text-xs" style={{ color: "var(--admin-text-secondary)" }}>
                  صفحه {formatNumber(page)} از {formatNumber(totalPages)}
                </span>
                <button
                  type="button"
                  className="admin-btn admin-btn-secondary admin-btn-sm"
                  disabled={page >= totalPages || isFetching}
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                >
                  بعدی
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Factor detail drawer */}
      <AdminFactorDrawer factor={selectedFactor} onClose={() => setSelectedFactor(null)} />
    </div>
  );
}